// Angular Import
import { inject, ErrorHandler, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// project import
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService
} from '@azure/msal-angular';
import { BrowserCacheLocation, IPublicClientApplication, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { FullCalendarModule } from '@fullcalendar/angular';
import { ActiveReportsModule } from '@mescius/activereportsjs-angular';
import { SvgIconComponent, provideAngularSvgIcon } from 'angular-svg-icon';
import {
  IgxButtonGroupModule,
  IgxButtonModule,
  IgxCardModule,
  IgxCheckboxModule,
  IgxChipsModule,
  IgxColumnComponent,
  IgxComboModule,
  IgxDatePickerModule,
  IgxDialogModule,
  IgxDropDownComponent,
  IgxDropDownItemComponent,
  IgxExcelExporterService,
  IgxExpansionPanelModule,
  IgxGridComponent,
  IgxGridModule,
  IgxIconModule,
  IgxInputGroupModule,
  IgxLayoutModule,
  IgxNavigationDrawerModule,
  IgxRippleModule,
  IgxSelectModule,
  IgxSimpleComboModule,
  IgxTextSelectionModule,
  IgxTimePickerModule,
  IgxToastModule,
  IgxToggleModule,
  IgxTooltipModule,
  IgxTreeModule
} from 'igniteui-angular';
import { NgxColorsModule } from 'ngx-colors';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { environment } from 'src/environments/environment';
import { Globals } from 'src/globals';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { DropdownOpenDirectiveModule } from './core/directives/dropdown-open.directive';
import { InputBoxTrimDirectiveModule } from './core/directives/input-box-trim.directive';
import { LongPressDirectiveModule } from './core/directives/long-press.directive';
import { IgxPreventDocumentScrollModule } from './core/directives/prevent-scroll.directive';
import { AuthGuard } from './core/guards/auth.guard';
import { CustomMsalInterceptor } from './core/interseptors/msal.interceptor';
import { ApplicationPipesModule } from './core/pipes/application-pipes-module';
import { AdminDashboardComponent } from './pages/admin-dashboard/admin-dashboard.component';
import { AddActivityComponent } from './pages/dialogs/add-activity/add-activity.component';
import { AlertDialogComponent } from './pages/dialogs/alert-dialog/alert-dialog.component';
import { AppointmentComponent } from './pages/dialogs/appointment/appointment.component';
import { AttachmentDialogComponent } from './pages/dialogs/attachment-dialog/attachment-dialog.component';
import { CommonDropdownComponent } from './pages/dialogs/common-dropdown/common-dropdown.component';
import { ConfirmationBoxComponent } from './pages/dialogs/confirmation-box/confirmation-box.component';
import { HouseOnHoldComponent } from './pages/dialogs/house-on-hold/house-on-hold.component';
import { ReportViewerComponent } from './pages/dialogs/report-viewer/report-viewer.component';
import { TelerikReportViewerDialogComponent } from './pages/dialogs/telerik-report-viewer/telerik-report-viewer.component';
import { TelerikReportingModule } from '@progress/telerik-angular-report-viewer';
import { SetPasswordComponent } from './pages/dialogs/set-password/set-password.component';
import { TransferHousesComponent } from './pages/dialogs/transfer-houses/transfer-houses.component';
import { HousePorequestContextMenuComponent } from './pages/house/porequest/porequest-context-menu/porequest-context-menu.component';
import { PorequestComponent } from './pages/house/porequest/porequest.component';
import { BlogComponent } from './pages/tasks/blog/blog/blog.component';
import { PoRequestHistoryComponent } from './pages/tasks/po-request/po-request-history/po-request-history.component';
import { AdminComponent } from './theme/layout/admin/admin.component';
import { ConfigurationComponent } from './theme/layout/admin/configuration/configuration.component';
import { NavBarComponent } from './theme/layout/admin/nav-bar/nav-bar.component';
import { NavLeftComponent } from './theme/layout/admin/nav-bar/nav-left/nav-left.component';
import { NavSearchComponent } from './theme/layout/admin/nav-bar/nav-left/nav-search/nav-search.component';
import { ChatUserListComponent } from './theme/layout/admin/nav-bar/nav-right/chat-user-list/chat-user-list.component';
import { FriendComponent } from './theme/layout/admin/nav-bar/nav-right/chat-user-list/friend/friend.component';
import { NavRightComponent } from './theme/layout/admin/nav-bar/nav-right/nav-right.component';
import { NavCollapseComponent } from './theme/layout/admin/navigation/nav-content/nav-collapse/nav-collapse.component';
import { NavContentComponent } from './theme/layout/admin/navigation/nav-content/nav-content.component';
import { NavGroupComponent } from './theme/layout/admin/navigation/nav-content/nav-group/nav-group.component';
import { NavItemComponent } from './theme/layout/admin/navigation/nav-content/nav-item/nav-item.component';
import { NavigationComponent } from './theme/layout/admin/navigation/navigation.component';
import { GuestComponent } from './theme/layout/guest/guest.component';
import { LoadingComponent } from './theme/shared/components/loader/loader.component';
import { SharedModule } from './theme/shared/shared.module';
import { ToasterContainerComponent } from './toaster/toaster-container/toaster-container.component';
import ToasterComponent from './toaster/toaster.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { CustomRedirectComponent } from './redirect/redirect.component';
import { VendorConsentComponent } from './vendor-consent/vendor-consent.component';
import { GlobalErrorHandler } from './core/global-error-handler/global-error-handler';
import { CursorToStartDirectiveModule } from './core/directives/cursor-to-start.directive';
import { ConfirmationSubmitComponent } from './pages/dialogs/confirmation-submit/confirmation-submit.component';
import { LongPressTouchDirectiveModule } from './core/directives/long-press-touch-directive';
import { PoRequestGridViewChangeComponent } from './pages/dialogs/po-request-grid-view-change/po-request-grid-view-change.component';
import { AlertErrorListComponent } from './pages/dialogs/alert-error-list/alert-error-list.component';
import { NumericInputDirectiveModule } from './core/directives/numeric-input.directive';
import { ImageEditComponent } from './pages/dialogs/image-edit/image-edit.component';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { GlobalErrorHandlerService } from "./core/services/global-error-handler.service";

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: environment.msalConfig.auth,
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.graphConfig.scopes],
      state: btoa(JSON.stringify({ uri: window.location.origin, action: 'login' }))
    },
    loginFailedRoute: environment.msalConfig.loginFailedRoute
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(environment.graphConfig.uri, [...environment.graphConfig.scopes]);
  protectedResourceMap.set(`${environment.apiConfig.uri}*`, [...environment.apiConfig.scopes]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

import { SimpleComboSelectionModule } from 'src/app/core/directives/simple-combo-selection.module';
import { ContentWidthDirectiveModule } from "./core/directives/content-width.directive";
@NgModule({
  declarations: [
    LoadingComponent,
    AppComponent,
    AdminComponent,
    GuestComponent,
    ConfigurationComponent,
    NavBarComponent,
    NavigationComponent,
    NavLeftComponent,
    NavRightComponent,
    NavSearchComponent,
    ChatUserListComponent,
    FriendComponent,
    NavContentComponent,
    NavItemComponent,
    NavCollapseComponent,
    NavGroupComponent,
    AdminDashboardComponent,
    ToasterContainerComponent,
    ToasterComponent,
    PoRequestHistoryComponent,
    CommonDropdownComponent,
    SetPasswordComponent,
    HouseOnHoldComponent,
    TransferHousesComponent,
    ConfirmationBoxComponent,
    ImageEditComponent,
    AlertDialogComponent,
    PorequestComponent,
    AttachmentDialogComponent,
    AppointmentComponent,
    ReportViewerComponent,
    TelerikReportViewerDialogComponent,
    BlogComponent,
    HousePorequestContextMenuComponent,
    // UnauthorizedComponent,
    AddActivityComponent,
    CustomRedirectComponent,
    VendorConsentComponent,
    ConfirmationSubmitComponent,
    PoRequestGridViewChangeComponent,
    AlertErrorListComponent
  ],
  imports: [
    MatButtonModule,
    MatToolbarModule,
    MatListModule,
    MatMenuModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    BrowserModule,
    CommonModule,
    NgbNavModule,
    NgScrollbarModule,
    AppRoutingModule,
    SharedModule,
    CoreModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    SvgIconComponent,
    IgxButtonModule,
    IgxIconModule,
    IgxLayoutModule,
    IgxNavigationDrawerModule,
    IgxRippleModule,
    IgxToggleModule,
    IgxTreeModule,
    IgxExpansionPanelModule,
    IgxChipsModule,
    IgxToastModule,
    MsalModule,
    IgxComboModule,
    IgxSimpleComboModule,
    IgxCardModule,
    IgxInputGroupModule,
    IgxGridModule,
    NgxColorsModule,
    IgxCheckboxModule,
    IgxDialogModule,
    IgxSelectModule,
    IgxDatePickerModule,
    IgxColumnComponent,
    IgxPreventDocumentScrollModule,
    ApplicationPipesModule,
    IgxTextSelectionModule,
    IgxGridComponent,
    ActiveReportsModule,
    IgxTimePickerModule,
    InputBoxTrimDirectiveModule,
    DropdownOpenDirectiveModule,
    CursorToStartDirectiveModule,
    ContentWidthDirectiveModule,
    LongPressDirectiveModule,
    LongPressTouchDirectiveModule,
    NumericInputDirectiveModule,
    DragDropModule,
    IgxDropDownComponent,
    IgxDropDownItemComponent,
    MatChipsModule,
    MatDialogModule,
    TelerikReportingModule
  ],
  exports: [
    MatAutocompleteModule,
    MatBadgeModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatStepperModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    MatFormFieldModule,
    FullCalendarModule,
    IgxComboModule,
    IgxSimpleComboModule,
    IgxButtonGroupModule,
    IgxCardModule,
    NgxColorsModule,
    IgxCheckboxModule,
    IgxDialogModule,
    IgxIconModule,
    ActiveReportsModule,
    IgxTimePickerModule,
    IgxGridModule,
    IgxTooltipModule,
    CustomRedirectComponent
  ],
  bootstrap: [AppComponent],
  providers: [
    IgxExcelExporterService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomMsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    provideAppInitializer(() => inject(MsalService).instance.initialize()),
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    DatePipe,
    MsalService,
    MsalGuard,
    AuthGuard,
    MsalBroadcastService,
    Globals,
    GlobalErrorHandler,
    provideAngularSvgIcon(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }
  ]
})
export class AppModule { }
